import React, { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import {
  Building2, Mail, Phone, Globe, User, Lock, Eye, EyeOff,
  ImagePlus, Camera, AlertCircle, CheckCircle2, ExternalLink,
  GraduationCap, Sparkles, Link2, BadgeCheck, Loader2, ArrowLeft,
} from "lucide-react";

const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || "theskillgenie.com";

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:      "1px solid #E2E8F0",
  radius:      { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:      { xs: "0 1px 3px rgba(0,0,0,0.05)", sm: "0 2px 8px rgba(0,0,0,0.07)", md: "0 8px 30px rgba(0,0,0,0.08)", lg: "0 8px 40px rgba(79, 70, 229,0.13)" },
  brand:       "var(--primary-blue, #4F46E5)",
  brandLight:  "rgba(79, 70, 229,0.08)",
  brandBorder: "rgba(79, 70, 229,0.18)",
  text: {
    heading:  "#0F172A",
    body:     "#334155",
    muted:    "#475569",
    disabled: "#94A3B8",
  },
  bg: { main: "#F8FAFC", section: "#FFFFFF" },
};

// --- Helpers ------------------------------------------------------------------
function validateTenantName(name) {
  if (!name) return "Portal name is required.";
  if (name.length < 2) return "Must be at least 2 characters.";
  if (name.length > 63) return "Must be 63 characters or less.";
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(name) && !/^[a-z0-9]$/.test(name))
    return "Only lowercase letters, digits, and hyphens (not at start/end).";
  if (/--/.test(name)) return "No consecutive hyphens allowed.";
  return null;
}

function pwStrength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

const strengthMeta = [
  { label: "Too short", color: "#EF4444" },
  { label: "Weak",      color: "#F97316" },
  { label: "Fair",      color: "#EAB308" },
  { label: "Good",      color: "#22C55E" },
  { label: "Strong",    color: "#16A34A" },
];

const COUNTRIES = [
  "India", "United States", "United Kingdom", "Canada", "Australia", 
  "Singapore", "Germany", "France", "Japan", "Other"
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", 
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", 
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", 
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", 
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
].sort();

// --- Sub-components -----------------------------------------------------------
const inputBase = {
  width: "100%", padding: "11px 14px", fontSize: "14px", fontWeight: 600,
  color: "#0F172A", background: "#F8FAFC", border: "1.5px solid #E2E8F0",
  borderRadius: "12px", outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

const Field = ({ label, required, children, helperText }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <label style={{ fontSize: "14px", fontWeight: 600, color: T.text.heading, display: "flex", alignItems: "center" }}>
      {label}{required && <span style={{ color: "#EF4444", marginLeft: "4px" }}>*</span>}
    </label>
    {children}
    {helperText && (
      <p style={{ fontSize: "12px", color: T.text.muted, margin: 0, fontWeight: 500 }}>
        {helperText}
      </p>
    )}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = "text", focused, onFocus, onBlur, autoComplete, spellCheck }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    onFocus={onFocus}
    onBlur={onBlur}
    autoComplete={autoComplete}
    spellCheck={spellCheck}
    style={{
      ...inputBase,
      borderColor: focused ? T.brand : "#E2E8F0",
      boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
    }}
  />
);

const SelectInput = ({ value, onChange, options, placeholder, focused, onFocus, onBlur }) => (
  <select
    value={value}
    onChange={onChange}
    onFocus={onFocus}
    onBlur={onBlur}
    style={{
      ...inputBase,
      borderColor: focused ? T.brand : "#E2E8F0",
      boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748B' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "16px",
      paddingRight: "40px",
      cursor: "pointer",
    }}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>{opt}</option>
    ))}
  </select>
);

const PasswordInput = ({ value, onChange, placeholder, focused, onFocus, onBlur }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete="new-password"
        style={{
          ...inputBase,
          paddingRight: "44px",
          borderColor: focused ? T.brand : "#E2E8F0",
          boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
        }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        style={{
          position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", cursor: "pointer", padding: "4px",
          color: T.text.muted, display: "flex", alignItems: "center",
        }}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff style={{ width: "16px", height: "16px" }} /> : <Eye style={{ width: "16px", height: "16px" }} />}
      </button>
    </div>
  );
};

const StrengthBar = ({ password }) => {
  const s = pwStrength(password);
  if (!password) return null;
  const meta = strengthMeta[s];
  return (
    <div style={{ marginTop: "6px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{
            flex: 1, height: "3px", borderRadius: T.radius.full,
            background: i <= s ? meta.color : "#E2E8F0",
            transition: "background 0.2s",
          }} />
        ))}
      </div>
      <p style={{ fontSize: "11px", fontWeight: 600, color: meta.color, margin: 0 }}>{meta.label}</p>
    </div>
  );
};

// Section card
const Section = ({ title, icon: Icon, children, id, description }) => (
  <div id={id} style={{
    background: T.bg.section, border: T.border, borderRadius: T.radius.lg,
    boxShadow: T.shadow.md, marginBottom: "24px",
  }}>
    <div style={{ padding: "32px" }}>
      {title && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: description ? "6px" : "0" }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "8px",
              background: T.brandLight, display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              {Icon && <Icon style={{ width: "16px", height: "16px", color: T.brand }} />}
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: T.text.heading, margin: 0, letterSpacing: "-0.01em" }}>
              {title}
            </h3>
          </div>
          {description && (
            <p style={{ fontSize: "14px", color: T.text.muted, margin: 0, fontWeight: 500 }}>
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  </div>
);

// --- Success Modal ------------------------------------------------------------
const SuccessModal = ({ portalUrl, onClose }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 1000,
    background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
  }}>
    <div style={{
      background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
      boxShadow: "0 24px 64px rgba(0,0,0,0.18)", width: "100%", maxWidth: "460px",
      overflow: "hidden", animation: "modalIn 0.22s ease",
    }}>
      <div style={{ padding: "32px 32px 36px", textAlign: "center" }}>
        <div style={{
          width: "56px", height: "56px", borderRadius: T.radius.xl,
          background: "#F0FDF4", border: "1px solid #BBF7D0",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px",
        }}>
          <CheckCircle2 style={{ width: "26px", height: "26px", color: "#16A34A" }} />
        </div>

        <h2 style={{ fontSize: "22px", fontWeight: 800, color: T.text.heading, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
          Registration Successful!
        </h2>
        <p style={{ fontSize: "14px", color: T.text.muted, margin: "0 0 24px", fontWeight: 500 }}>
          Your institution has been registered. Your portal is live at:
        </p>

        <div style={{
          padding: "12px 16px", borderRadius: T.radius.lg,
          background: T.brandLight, border: `1px solid ${T.brandBorder}`,
          marginBottom: "24px",
        }}>
          <p style={{
            fontSize: "13px", fontWeight: 800, color: T.brand,
            margin: 0, wordBreak: "break-all", fontFamily: "monospace",
          }}>
            {portalUrl}/admin/login
          </p>
        </div>

        <p style={{ fontSize: "12px", color: T.text.disabled, margin: "0 0 24px" }}>
          Clicking below will take you to your admin login page.
        </p>

        <button
          onClick={onClose}
          style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "12px 32px", borderRadius: T.radius.md,
            background: T.brand, color: "#FFFFFF", border: "none",
            cursor: "pointer", fontSize: "14px", fontWeight: 800,
            letterSpacing: "0.04em", textTransform: "uppercase",
            boxShadow: "0 4px 16px rgba(79, 70, 229,0.32)",
          }}
        >
          <ExternalLink style={{ width: "14px", height: "14px" }} />
          Go to Admin Login
        </button>
      </div>
    </div>
    <style>{`@keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } } @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
  </div>
);

// --- CollegeRegister ----------------------------------------------------------
const CollegeRegister = () => {
  const navigate = useNavigate();
  const logoInputRef   = useRef(null);
  const imagesInputRef = useRef(null);

  const [form, setForm] = useState({
    college_name: "", state: "", country: "",
    email: "", phone: "", website: "",
    username: "", password: "", confirmPassword: "",
    tenant_name: "",
  });
  const [logo,               setLogo]               = useState(null);
  const [logoPreview,        setLogoPreview]         = useState(null);
  const [images,             setImages]              = useState([]);
  const [error,              setError]               = useState("");
  const [tenantError,        setTenantError]         = useState("");
  const [loading,            setLoading]             = useState(false);
  const [portalUrl,          setPortalUrl]           = useState("");
  const [showModal,          setShowModal]           = useState(false);
  const [focused,            setFocused]             = useState(null);

  // Lead Ambassador state
  const [hasReferral,        setHasReferral]         = useState(false);
  const [leadAmbassadorId,   setLeadAmbassadorId]   = useState("");
  const [ambassadorInfo,     setAmbassadorInfo]      = useState(null);  // { name, designation }
  const [ambassadorError,    setAmbassadorError]     = useState("");
  const [ambassadorValidating, setAmbassadorValidating] = useState(false);

  const previewUrl = form.tenant_name ? `https://${form.tenant_name}.${BASE_DOMAIN}` : "";

  const f = (field) => (e) => {
    if (field === "phone") {
      const cleaned = e.target.value.replace(/\D/g, "");
      if (cleaned.length <= 10) setForm((p) => ({ ...p, phone: cleaned }));
    } else if (field === "tenant_name") {
      const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
      setForm((p) => ({ ...p, tenant_name: cleaned }));
      setTenantError(validateTenantName(cleaned) || "");
    } else {
      setForm((p) => ({ ...p, [field]: e.target.value }));
    }
  };

  const validateAmbassadorId = useCallback(async (id) => {
    const trimmed = (id || "").trim().toUpperCase();
    if (!trimmed) {
      setAmbassadorInfo(null);
      setAmbassadorError("");
      return;
    }
    setAmbassadorValidating(true);
    setAmbassadorInfo(null);
    setAmbassadorError("");
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/lead-ambassadors/validate/${encodeURIComponent(trimmed)}`
      );
      if (res.data?.valid) {
        setAmbassadorInfo({ name: res.data.name, designation: res.data.designation });
        setAmbassadorError("");
      } else {
        setAmbassadorInfo(null);
        setAmbassadorError("Invalid or Inactive Partner Referral ID");
      }
    } catch {
      setAmbassadorInfo(null);
      setAmbassadorError("Invalid or Inactive Partner Referral ID");
    } finally {
      setAmbassadorValidating(false);
    }
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    setImages(files);
  };

  const scrollToField = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.phone && form.phone.length !== 10) {
      scrollToField("field-phone");
      return setError("Phone number must be exactly 10 digits.");
    }
    if (form.password !== form.confirmPassword) {
      scrollToField("field-confirm-password");
      return setError("Passwords do not match.");
    }
    const tnErr = validateTenantName(form.tenant_name);
    if (tnErr) { setTenantError(tnErr); scrollToField("section-portal"); return; }

    // Partner Referral validation gate (mandatory when checkbox is checked)
    const trimmedAmbId = hasReferral ? leadAmbassadorId.trim().toUpperCase() : "";
    if (hasReferral) {
      if (!trimmedAmbId) {
        scrollToField("section-ambassador");
        return setError("Partner Referral ID is required when referral option is selected.");
      }
      if (!ambassadorInfo) {
        await validateAmbassadorId(trimmedAmbId);
        scrollToField("section-ambassador");
        return setError("Please enter a valid Partner Referral ID before registering.");
      }
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("college_name",       form.college_name);
      fd.append("state",              form.state);
      fd.append("country",            form.country);
      fd.append("email",              form.email);
      fd.append("phone",              form.phone);
      fd.append("website",            form.website);
      fd.append("username",           form.username);
      fd.append("password",           form.password);
      fd.append("tenant_name",        form.tenant_name);
      if (trimmedAmbId) fd.append("lead_ambassador_id", trimmedAmbId);
      if (logo) fd.append("logo", logo);
      images.forEach((img) => fd.append("images", img));

      const res = await axios.post(`${API_BASE_URL}/colleges/register`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Update local storage user object if backend returns it
      if (res.data?.college) {
        localStorage.setItem("user", JSON.stringify(res.data.college));
      }

      const url = res.data?.portal_url || `https://${form.tenant_name}.${BASE_DOMAIN}`;
      setPortalUrl(url);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to register. Please try again.");
      setTimeout(() => scrollToField("error-banner"), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    window.location.href = portalUrl ? `${portalUrl}/admin/login` : "/collegelogin";
  };

  const fo = (id) => () => setFocused(id);
  const fb = ()    => setFocused(null);

  return (
    <div style={{ minHeight: "100vh", background: T.bg.main, fontFamily: "inherit" }}>

      {showModal && <SuccessModal portalUrl={portalUrl} onClose={handleModalClose} />}

      {/* -- Top nav bar ------------------------------------------------------- */}
      <header style={{
        position: "sticky", top: 0, zIndex: 40,
        background: T.bg.section, borderBottom: T.border,
        boxShadow: T.shadow.xs,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 28px", height: "60px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={() => navigate('/college-intro')}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "6px 12px", borderRadius: T.radius.md,
              background: "transparent", border: `1px solid ${T.brandBorder}`,
              color: T.brand, fontSize: "13px", fontWeight: 700,
              cursor: "pointer", transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = T.brandLight;
              e.currentTarget.style.transform = "translateX(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.transform = "translateX(0)";
            }}
          >
            <ArrowLeft style={{ width: "16px", height: "16px" }} />
            Back
          </button>
          <div style={{ width: "1px", height: "24px", background: "#E2E8F0" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "32px", height: "32px", borderRadius: T.radius.md,
              background: T.brandLight, border: `1px solid ${T.brandBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <GraduationCap style={{ width: "16px", height: "16px", color: T.brand }} />
            </div>
            <span style={{ fontSize: "15px", fontWeight: 800, color: T.text.heading, letterSpacing: "-0.02em" }}>
              The Skill Genie
            </span>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: "880px", margin: "0 auto", padding: "40px 24px 80px" }}>

        {/* -- Page heading -------------------------------------------------- */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            padding: "4px 14px", borderRadius: T.radius.full,
            background: T.brandLight, border: `1px solid ${T.brandBorder}`,
            fontSize: "11px", fontWeight: 700, color: T.brand,
            textTransform: "uppercase", letterSpacing: "0.06em",
            marginBottom: "16px",
          }}>
            <Sparkles style={{ width: "12px", height: "12px" }} />
            Enterprise Institution Onboarding
          </div>
          <h1 style={{
            fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: 900,
            color: T.text.heading, letterSpacing: "-0.04em", margin: "0 0 12px",
            lineHeight: 1.1,
          }}>
            Create Your Institution Workspace
          </h1>
          <p style={{ fontSize: "16px", color: T.text.muted, margin: 0, fontWeight: 500, lineHeight: 1.5, maxWidth: "720px" }}>
            Register your institution to access AI-powered learning, assessments, placements, and administration through The Skill Genie Enterprise Platform.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>

          {/* -- Error banner ------------------------------------------------ */}
          {error && (
            <div id="error-banner" style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "13px 16px", borderRadius: T.radius.lg,
              background: "#FEF2F2", border: "1px solid #FECACA", marginBottom: "20px",
            }}>
              <AlertCircle style={{ width: "15px", height: "15px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
              <p style={{ fontSize: "13px", color: "#DC2626", fontWeight: 600, margin: 0 }}>{error}</p>
            </div>
          )}

          {/* -- Card 1: Identity -------------------------------------------- */}
          <Section title="Institution Branding" icon={Building2}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "32px", flexWrap: "wrap", marginBottom: "32px" }}>
              {/* Logo */}
              <div style={{ flexShrink: 0 }}>
                <Field label="Institution Logo" helperText="PNG or JPG • Recommended 512×512">
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    style={{
                      width: "100px", height: "100px", borderRadius: T.radius.lg,
                      background: T.bg.main, border: logoPreview ? `2px solid ${T.brandBorder}` : "2px dashed #CBD5E1",
                      overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", position: "relative", boxShadow: T.shadow.xs,
                      transition: "border-color 0.15s",
                    }}
                  >
                    {logoPreview
                      ? <img src={logoPreview} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }} />
                      : <div style={{ textAlign: "center", padding: "12px" }}>
                          <Camera style={{ width: "24px", height: "24px", color: T.text.disabled, display: "block", margin: "0 auto 6px" }} />
                          <span style={{ fontSize: "10px", fontWeight: 700, color: T.text.disabled, textTransform: "uppercase", letterSpacing: "0.04em" }}>Upload Logo</span>
                        </div>
                    }
                    {logoPreview && (
                      <div style={{
                        position: "absolute", inset: 0, background: "rgba(79, 70, 229,0.1)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        opacity: 0, transition: "opacity 0.15s",
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = "0"; }}
                      >
                        <Camera style={{ width: "20px", height: "20px", color: T.brand }} />
                      </div>
                    )}
                  </div>
                </Field>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: "none" }} />
              </div>

              {/* Name + location */}
              <div style={{ flex: 1, minWidth: "280px", display: "flex", flexDirection: "column", gap: "20px" }}>
                <Field label="Full Name of Institution" required>
                  <TextInput
                    value={form.college_name} onChange={f("college_name")}
                    placeholder="Example: Sri Venkateswara Engineering College"
                    focused={focused === "college_name"} onFocus={fo("college_name")} onBlur={fb}
                  />
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <Field label="Country">
                    <SelectInput
                      value={form.country} onChange={(e) => {
                        f("country")(e);
                        if (e.target.value !== "India") {
                          setForm(p => ({ ...p, state: "" }));
                        }
                      }}
                      options={COUNTRIES}
                      placeholder="Select Country"
                      focused={focused === "country"} onFocus={fo("country")} onBlur={fb}
                    />
                  </Field>
                  <Field label="State">
                    {!form.country ? (
                      <input
                        disabled
                        placeholder="Select Country First"
                        style={{
                          ...inputBase,
                          background: "#F1F5F9",
                          color: T.text.disabled,
                          cursor: "not-allowed",
                          borderColor: "#E2E8F0",
                        }}
                      />
                    ) : form.country === "India" ? (
                      <SelectInput
                        value={form.state} onChange={f("state")} options={INDIAN_STATES}
                        placeholder="Select State"
                        focused={focused === "state"} onFocus={fo("state")} onBlur={fb}
                      />
                    ) : (
                      <TextInput
                        value={form.state} onChange={f("state")} placeholder="Select State"
                        focused={focused === "state"} onFocus={fo("state")} onBlur={fb}
                      />
                    )}
                  </Field>
                </div>
              </div>
            </div>

            {/* Contact grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "20px" }}>
              <Field label="Official Email" required>
                <div style={{ position: "relative" }}>
                  <Mail style={{
                    position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                    width: "16px", height: "16px", color: focused === "email" ? T.brand : T.text.disabled,
                    pointerEvents: "none", transition: "color 0.15s",
                  }} />
                  <input
                    type="email" value={form.email} onChange={f("email")}
                    placeholder="official@institution.edu"
                    onFocus={fo("email")} onBlur={fb}
                    style={{
                      ...inputBase, paddingLeft: "42px",
                      borderColor: focused === "email" ? T.brand : "#E2E8F0",
                      boxShadow:   focused === "email" ? `0 0 0 3px ${T.brandLight}` : "none",
                    }}
                  />
                </div>
              </Field>
              <div id="field-phone">
              <Field label="Contact Number">
                <div style={{ position: "relative" }}>
                  <Phone style={{
                    position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                    width: "16px", height: "16px", color: focused === "phone" ? T.brand : T.text.disabled,
                    pointerEvents: "none", transition: "color 0.15s",
                  }} />
                  <input
                    type="text" value={form.phone} onChange={f("phone")}
                    placeholder="Enter Official Contact Number"
                    onFocus={fo("phone")} onBlur={fb}
                    style={{
                      ...inputBase, paddingLeft: "42px",
                      borderColor: focused === "phone" ? T.brand : "#E2E8F0",
                      boxShadow:   focused === "phone" ? `0 0 0 3px ${T.brandLight}` : "none",
                    }}
                  />
                </div>
              </Field>
              </div>
              <Field label="Website URL">
                <div style={{ position: "relative" }}>
                  <Globe style={{
                    position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                    width: "16px", height: "16px", color: focused === "website" ? T.brand : T.text.disabled,
                    pointerEvents: "none", transition: "color 0.15s",
                  }} />
                  <input
                    type="url" value={form.website} onChange={f("website")}
                    placeholder="https://www.yourinstitution.edu"
                    onFocus={fo("website")} onBlur={fb}
                    style={{
                      ...inputBase, paddingLeft: "42px",
                      borderColor: focused === "website" ? T.brand : "#E2E8F0",
                      boxShadow:   focused === "website" ? `0 0 0 3px ${T.brandLight}` : "none",
                    }}
                  />
                </div>
              </Field>
            </div>
          </Section>

          {/* -- Referral toggle checkbox --------------------------------------- */}
          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="has-referral-checkbox"
              style={{
                display: "inline-flex", alignItems: "center", gap: "10px",
                cursor: "pointer", userSelect: "none",
                fontSize: "14px", fontWeight: 600, color: T.text.body,
              }}
            >
              <div style={{ position: "relative", flexShrink: 0 }}>
                <input
                  id="has-referral-checkbox"
                  type="checkbox"
                  checked={hasReferral}
                  onChange={(e) => {
                    setHasReferral(e.target.checked);
                    if (!e.target.checked) {
                      setLeadAmbassadorId("");
                      setAmbassadorInfo(null);
                      setAmbassadorError("");
                    }
                  }}
                  style={{ position: "absolute", opacity: 0, width: "18px", height: "18px", cursor: "pointer", margin: 0 }}
                />
                <div style={{
                  width: "18px", height: "18px", borderRadius: "5px",
                  border: hasReferral ? `2px solid ${T.brand}` : "2px solid #CBD5E1",
                  background: hasReferral ? T.brand : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.18s ease",
                }}>
                  {hasReferral && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              I have a Referral / Partner Code
            </label>
          </div>

          {/* -- Card 2: Referral Information (shown only when checkbox checked) -- */}
          <div
            id="section-ambassador"
            style={{
              overflow: "hidden",
              maxHeight: hasReferral ? "500px" : "0px",
              opacity: hasReferral ? 1 : 0,
              transition: "max-height 0.28s ease, opacity 0.22s ease",
              marginBottom: hasReferral ? "0" : "-24px",
            }}
          >
            <Section
              title="Referral Information"
              icon={BadgeCheck}
              description="Have a referral or sales partner code? Enter it below."
            >
              <Field label="Partner Referral ID" required helperText="Accepted formats: EXITSG-*">
                <div style={{ position: "relative" }}>
                  <BadgeCheck style={{
                    position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                    width: "16px", height: "16px",
                    color: ambassadorInfo ? "#16A34A" : focused === "lead_ambassador_id" ? T.brand : T.text.disabled,
                    pointerEvents: "none", transition: "color 0.15s",
                  }} />
                  <input
                    type="text"
                    value={leadAmbassadorId}
                    onChange={(e) => {
                      setLeadAmbassadorId(e.target.value.toUpperCase());
                      setAmbassadorInfo(null);
                      setAmbassadorError("");
                    }}
                    onBlur={() => { fb(); validateAmbassadorId(leadAmbassadorId); }}
                    onFocus={fo("lead_ambassador_id")}
                    placeholder="Enter Referral Code"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={!hasReferral}
                    style={{
                      ...inputBase,
                      paddingLeft: "42px",
                      paddingRight: ambassadorValidating ? "40px" : "14px",
                      borderColor: ambassadorError
                        ? "#EF4444"
                        : ambassadorInfo
                        ? "#16A34A"
                        : focused === "lead_ambassador_id" ? T.brand : "#E2E8F0",
                      boxShadow: ambassadorError
                        ? "0 0 0 3px rgba(239,68,68,0.1)"
                        : ambassadorInfo
                        ? "0 0 0 3px rgba(22,163,74,0.1)"
                        : focused === "lead_ambassador_id" ? `0 0 0 3px ${T.brandLight}` : "none",
                    }}
                  />
                  {ambassadorValidating && (
                    <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }}>
                      <Loader2 style={{ width: "15px", height: "15px", color: T.brand, animation: "spin 1s linear infinite" }} />
                    </div>
                  )}
                </div>

                {/* Valid ambassador badge */}
                {ambassadorInfo && !ambassadorError && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    marginTop: "8px", padding: "9px 14px",
                    background: "#F0FDF4", border: "1px solid #BBF7D0",
                    borderRadius: "10px",
                  }}>
                    <CheckCircle2 style={{ width: "15px", height: "15px", color: "#16A34A", flexShrink: 0 }} />
                    <div>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#15803D" }}>
                        {ambassadorInfo.name}
                      </p>
                      {ambassadorInfo.designation && (
                        <p style={{ margin: 0, fontSize: "11px", fontWeight: 500, color: "#16A34A" }}>
                          {ambassadorInfo.designation}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Invalid ambassador error */}
                {ambassadorError && (
                  <div style={{ display: "flex", alignItems: "center", gap: "7px", marginTop: "6px" }}>
                    <AlertCircle style={{ width: "13px", height: "13px", color: "#EF4444", flexShrink: 0 }} />
                    <p style={{ fontSize: "12px", color: "#EF4444", fontWeight: 600, margin: 0 }}>{ambassadorError}</p>
                  </div>
                )}
              </Field>
            </Section>
          </div>

          {/* -- Card 3: Primary Administrator Account --------------------------------------- */}
          <Section
            title="Primary Administrator Account"
            icon={User}
            description="This account will have full administrative access to your institution workspace."
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "20px" }}>
              <Field label="Admin Username" required>
                <div style={{ position: "relative" }}>
                  <User style={{
                    position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                    width: "16px", height: "16px", color: focused === "username" ? T.brand : T.text.disabled,
                    pointerEvents: "none", transition: "color 0.15s",
                  }} />
                  <input
                    type="text" value={form.username} onChange={f("username")}
                    placeholder="Choose a Username"
                    autoComplete="username"
                    onFocus={fo("username")} onBlur={fb}
                    style={{
                      ...inputBase, paddingLeft: "42px",
                      borderColor: focused === "username" ? T.brand : "#E2E8F0",
                      boxShadow:   focused === "username" ? `0 0 0 3px ${T.brandLight}` : "none",
                    }}
                  />
                </div>
              </Field>

              <Field label="Password" required>
                <PasswordInput
                  value={form.password} onChange={f("password")} placeholder="Create a Secure Password"
                  focused={focused === "password"} onFocus={fo("password")} onBlur={fb}
                />
                <StrengthBar password={form.password} />
              </Field>

              <div id="field-confirm-password">
              <Field label="Confirm Password" required>
                <PasswordInput
                  value={form.confirmPassword} onChange={f("confirmPassword")} placeholder="Confirm Your Password"
                  focused={focused === "confirmPassword"} onFocus={fo("confirmPassword")} onBlur={fb}
                />
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p style={{ fontSize: "11px", color: "#EF4444", fontWeight: 600, margin: "5px 0 0" }}>
                    Passwords do not match
                  </p>
                )}
              </Field>
              </div>
            </div>
          </Section>

          {/* -- Card 4: Institution Portal Address ---------------------------------- */}
          <Section
            title="Institution Portal Address"
            icon={Link2}
            id="section-portal"
            description="Choose a unique workspace URL for your institution."
          >
            <Field
              label="Workspace URL"
              required
              helperText={`Your portal will be available as: ${form.tenant_name || "yourinstitution"}.${BASE_DOMAIN}. Use lowercase letters, numbers, and hyphens only.`}
            >
              <TextInput
                value={form.tenant_name} onChange={f("tenant_name")}
                placeholder="yourinstitution"
                focused={focused === "tenant_name"} onFocus={fo("tenant_name")} onBlur={fb}
                autoComplete="off" spellCheck={false}
              />
            </Field>

            {/* Live URL preview removed as helperText handles it better now, or keep it if it looks premium */}
            {previewUrl && !tenantError && (
              <div style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "12px 16px", borderRadius: T.radius.md, marginTop: "16px",
                background: T.brandLight, border: `1px solid ${T.brandBorder}`,
              }}>
                <ExternalLink style={{ width: "16px", height: "16px", color: T.brand, flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: "11px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Portal URL &middot;{" "}
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: T.brand, fontFamily: "monospace", wordBreak: "break-all" }}>
                    {previewUrl}
                  </span>
                </div>
              </div>
            )}

            {tenantError && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                <AlertCircle style={{ width: "13px", height: "13px", color: "#EF4444", flexShrink: 0 }} />
                <p style={{ fontSize: "12px", color: "#EF4444", fontWeight: 600, margin: 0 }}>{tenantError}</p>
              </div>
            )}
          </Section>

          {/* -- Card 5: Campus Gallery -------------------------------------- */}
          <Section
            title="Campus Gallery"
            icon={ImagePlus}
            description="Showcase your institution by uploading up to 3 campus images."
          >
            <button
              type="button"
              onClick={() => imagesInputRef.current?.click()}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "12px 24px", borderRadius: T.radius.md,
                background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                color: T.brand, cursor: "pointer", fontSize: "14px", fontWeight: 700,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(79, 70, 229,0.13)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.brandLight; }}
            >
              <ImagePlus style={{ width: "16px", height: "16px" }} />
              Upload Campus Images
            </button>
            <input
              ref={imagesInputRef}
              type="file" accept="image/*" multiple
              onChange={handleImagesChange}
              style={{ display: "none" }}
            />

            {images.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <p style={{ fontSize: "11px", fontWeight: 700, color: T.brand, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                  {images.length} photo{images.length !== 1 ? "s" : ""} selected
                </p>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {images.map((file, idx) => (
                    <div key={idx} style={{
                      width: "96px", height: "64px", borderRadius: T.radius.lg,
                      overflow: "hidden", border: T.border, boxShadow: T.shadow.xs,
                    }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Campus ${idx + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* -- Submit ------------------------------------------------------ */}
          <button
            type="submit"
            disabled={loading}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              width: "100%", padding: "18px 32px", marginTop: "32px",
              background: loading ? "#93C5FD" : T.brand,
              color: "#FFFFFF", border: "none", borderRadius: T.radius.lg,
              fontSize: "16px", fontWeight: 700, letterSpacing: "0.02em",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 8px 24px rgba(79, 70, 229,0.25)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(79, 70, 229,0.35)"; } }}
            onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(79, 70, 229,0.25)"; } }}
          >
            {loading ? (
              <Loader2 style={{ width: "20px", height: "20px", animation: "spin 1s linear infinite" }} />
            ) : (
              <Building2 style={{ width: "20px", height: "20px" }} />
            )}
            {loading ? "Creating Workspace..." : "Create Institution Workspace"}
          </button>

          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <p style={{ fontSize: "15px", color: T.text.muted, fontWeight: 500 }}>
              Already have an institution workspace?{" "}
              <span style={{ color: T.text.muted, fontWeight: 500 }}>
                Sign in using your institution's dedicated portal URL.
              </span>
            </p>
          </div>


        </form>
      </main>
    </div>
  );
};

export default CollegeRegister;


